import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProjectProvider } from '../contexts/ProjectContext';
import { useAuth } from '../hooks/useAuth';
import { usePageMeta } from '../hooks/usePageMeta';
import { trackerApi } from '../services/devtoolsApi';
import { Sidebar } from './Sidebar';
import { SystemNoticesStrip } from './SystemNoticesPanel';
import { TopBar } from './TopBar';

const MOBILE_MAX = 768;

/** Viewport ≤768px — drives hamburger + off-canvas drawer vs fixed rail. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX : false,
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_MAX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

/**
 * Authenticated chrome: TopBar + responsive Sidebar (rail / drawer) + main outlet.
 * Login does not use this shell. Drawer closes on Escape, route change, or desktop resize.
 * Kitchen visitor sessions get a read-only status banner under the top bar (no second chrome);
 * members see a critical system-notices strip in the same slot when forge/Slack/Bugsnag fails.
 */
export function Layout() {
  const { t } = useTranslation();
  const { isVisitor, user } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Auth console is never indexable — keep tenant WIP out of search.
  usePageMeta({
    title: t('seo.console.title'),
    description: t('seo.console.description'),
    path: location.pathname,
    robots: 'noindex, nofollow',
  });

  // 1. Close drawer when crossing to desktop or after route change on mobile.
  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // 2. Escape dismisses the mobile drawer while open.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  const closeNav = useCallback(() => setMobileNavOpen(false), []);
  const toggleNav = useCallback(() => setMobileNavOpen((o) => !o), []);

  // Team-scoped Information Center feed for the critical TopBar strip (skipped for kitchen visitors).
  const systemNotices = useQuery({
    queryKey: ['system-notices'],
    queryFn: () => trackerApi.systemNotices(),
    enabled: !isVisitor,
    refetchInterval: 60_000,
  });

  return (
    <ProjectProvider>
      <div className="min-h-screen bg-[var(--background)]">
        <TopBar
          mobileNavOpen={mobileNavOpen}
          onToggleMobileNav={toggleNav}
          showMenuButton={isMobile}
        />

        {/* Dim overlay behind the mobile drawer */}
        {isMobile && mobileNavOpen ? (
          <button
            type="button"
            className="fixed inset-0 z-[1002] bg-[var(--shell-overlay)] md:hidden"
            aria-label={t('nav.closeMenu')}
            onClick={closeNav}
          />
        ) : null}

        <Sidebar mobileOpen={mobileNavOpen} onNavigate={closeNav} />

        <main
          className="min-w-0 pt-[var(--shell-topbar)] md:pl-[var(--shell-rail)]"
          aria-label={t('nav.main')}
        >
          {isVisitor ? (
            <div
              className="border-b border-[var(--border)] bg-[var(--surface-muted)] px-4 py-2 text-sm text-[var(--muted)] md:px-6"
              role="status"
            >
              {t('common.visitorBanner', { name: user?.name || t('common.dash') })}
            </div>
          ) : (
            <SystemNoticesStrip notices={systemNotices.data?.notices} />
          )}
          {/* Console is a full-bleed three-pane workspace inside the rail. */}
          <div
            className={
              location.pathname === '/console' || location.pathname.startsWith('/console/')
                ? 'h-[calc(100vh-var(--shell-topbar))] p-0'
                : 'p-4 md:p-6'
            }
          >
            <Outlet />
          </div>
        </main>
      </div>
    </ProjectProvider>
  );
}
